import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, Pause, RefreshCw, Terminal, Activity, Send, Calendar, X, ExternalLink, Bot } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { timeAgo } from "@/lib/format";

const SCHEDULES = [
  ["every_hour", "Every hour"], ["every_2_hours", "Every 2 hours"],
  ["every_4_hours", "Every 4 hours"], ["every_6_hours", "Every 6 hours"],
  ["custom", "Custom"], ["paused", "Paused"],
];

const AUTO_COUNTERS = [
  ["eligible", "Eligible", "text-cyan"],
  ["researching", "Researching", "text-amber"],
  ["ready", "Ready", "text-emerald"],
  ["drafting", "Drafting", "text-amber"],
  ["ai_rewrite", "AI Rewrite", "text-amber"],
  ["fact_checking", "Fact Checking", "text-amber"],
  ["published_auto", "Published Auto", "text-emerald"],
  ["human_review", "Human Review", "text-crimson"],
  ["rejected", "Rejected", "text-admmuted"],
  ["errors", "Errors", "text-crimson"],
];
const STAGE_LABEL = {
  queued: "Queued", researching: "Researching", drafting: "Drafting",
  rewriting: "AI Rewrite", fact_checking: "Fact Check", ready: "Ready to Auto Publish",
  published: "Published", human_review: "Human Review", rejected: "Rejected", error: "Error",
};
const STAGE_STYLE = {
  ready: "bg-emerald/15 text-emerald", published: "bg-emerald/15 text-emerald",
  human_review: "bg-crimson/15 text-crimson", rejected: "bg-admborder text-admmuted",
  error: "bg-crimson/15 text-crimson", researching: "bg-amber/15 text-amber",
  drafting: "bg-amber/15 text-amber", rewriting: "bg-amber/15 text-amber",
  fact_checking: "bg-amber/15 text-amber", queued: "bg-cyan/15 text-cyan",
};

export default function AIDesk() {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [awaiting, setAwaiting] = useState([]);
  const [published, setPublished] = useState([]);
  const [radar, setRadar] = useState(null);
  const [auto, setAuto] = useState(null);
  const [autoRunning, setAutoRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/agent/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/agent/runs").then((r) => setRuns(r.data || [])).catch(() => {});
    api.get("/agent/radar").then((r) => setRadar(r.data)).catch(() => {});
    api.get("/agent/auto/status").then((r) => setAuto(r.data)).catch(() => {});
    api.get("/admin/articles?status=awaiting_review").then((r) => setAwaiting(r.data || [])).catch(() => {});
    api.get("/admin/articles?status=published").then((r) => setPublished((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);

  const config = status?.config || {};
  const stats = status?.stats || {};
  const lastRun = status?.last_run;

  const runNow = async () => {
    setRunning(true);
    try { await api.post("/agent/run"); toast.success("Research run started — this may take a minute."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setTimeout(() => { setRunning(false); load(); }, 1500); }
  };
  const setPaused = async (paused) => {
    await api.put("/agent/config", { paused });
    toast.success(paused ? "Agent paused" : "Agent resumed"); load();
  };
  const setSchedule = async (schedule) => { await api.put("/agent/config", { schedule }); toast.success("Schedule updated"); load(); };
  const toggleAutopub = async (auto_publish) => { await api.put("/agent/config", { auto_publish }); load(); };
  const runAuto = async () => {
    setAutoRunning(true);
    try { await api.post("/agent/auto/run"); toast.success("Auto Newsroom started on ALL eligible stories."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setTimeout(() => { setAutoRunning(false); load(); }, 1500); }
  };
  const toggleAutoNewsroom = async (v) => {
    await api.put("/agent/config", { auto_newsroom_enabled: v });
    toast.success(v ? "Auto Newsroom enabled" : "Auto Newsroom disabled"); load();
  };

  const STAT_CARDS = [
    ["candidates", "Candidate Stories", "text-crimson"],
    ["drafts", "Drafts Created", "text-cyan"],
    ["awaiting", "Awaiting Approval", "text-amber"],
    ["fact_warnings", "Fact-Check Warnings", "text-crimson"],
    ["published", "Recently Published", "text-emerald"],
  ];

  const approve = async (id, status, override = false) => {
    let extra = {};
    if (status === "scheduled") {
      const dt = window.prompt("Schedule for (ISO, e.g. 2026-07-01T09:00:00Z):");
      if (!dt) return; extra = { scheduled_for: dt };
    }
    if (override) extra.override = true;
    try {
      await api.post(`/admin/articles/${id}/status`, { status, ...extra });
      toast.success(status === "published" ? "Approved & published" : status === "scheduled" ? "Scheduled" : "Rejected");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (!override && status === "published" && /override/i.test(detail)) {
        if (window.confirm(`⚠️ ${detail}\n\nOverride the editorial safety gate and publish anyway?`)) return approve(id, status, true);
        return;
      }
      toast.error("Action failed");
    }
  };

  return (
    <AdminLayout title="AI Editorial Desk">
      {/* Agent status bar */}
      <div className="bg-admcard border border-admborder p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-11 h-11 flex items-center justify-center border ${config.paused ? "border-admborder" : "border-emerald"}`}>
            <Activity size={20} className={config.paused ? "text-admmuted" : "text-emerald animate-pulse-dot"} />
          </div>
          <div>
            <div className="text-sm font-semibold text-admtext flex items-center gap-2">
              THE FIN DESK EDITOR
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${config.paused ? "bg-admborder text-admtext2" : "bg-emerald/15 text-emerald"}`}>
                {config.paused ? "Paused" : "Active"}
              </span>
            </div>
            <div className="text-xs text-admmuted font-mono mt-0.5">
              Last run: {lastRun ? timeAgo(lastRun.started_at) : "never"} · Auto-publish: OFF by default
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={runNow} disabled={running || config.paused} data-testid="agent-run-now"
            className="flex items-center gap-2 bg-cyan text-admbg px-4 py-2 text-xs font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-50">
            <RefreshCw size={14} className={running ? "animate-spin" : ""} /> Run Research Now
          </button>
          {config.paused ? (
            <button onClick={() => setPaused(false)} data-testid="agent-resume" className="flex items-center gap-2 border border-admborder text-admtext px-4 py-2 text-xs font-bold uppercase tracking-wider hover:border-emerald"><Play size={14} /> Resume</button>
          ) : (
            <button onClick={() => setPaused(true)} data-testid="agent-pause" className="flex items-center gap-2 border border-admborder text-admtext px-4 py-2 text-xs font-bold uppercase tracking-wider hover:border-amber"><Pause size={14} /> Pause</button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        {STAT_CARDS.map(([k, label, color]) => (
          <div key={k} className="bg-admcard border border-admborder p-4" data-testid={`agent-stat-${k}`}>
            <div className={`font-mono text-3xl ${color}`}>{stats[k] ?? 0}</div>
            <div className="text-xs text-admtext2 uppercase tracking-wider mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* News Radar — last 24h discovery intelligence */}
      <div className="bg-admcard border border-admborder p-5 mt-4" data-testid="news-radar-dashboard">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 flex items-center gap-2"><Activity size={14} /> News Radar</h3>
          <span className="text-[10px] uppercase tracking-wider text-admmuted">Last discovery cycle{radar?.last_run?.started_at ? ` · ${timeAgo(radar.last_run.started_at)}` : ""}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {[
            ["Sources Scanned", radar?.last_run?.sources_checked, "text-admtext"],
            ["Healthy Sources", radar?.sources?.healthy, "text-emerald"],
            ["Raw Developments", radar?.last_run?.raw_developments, "text-admtext"],
            ["Story Clusters", radar?.last_run?.story_clusters, "text-cyan"],
            ["Duplicates Merged", radar?.last_run?.duplicates_merged, "text-admtext2"],
            ["Primary Detected", radar?.last_run?.primary_detected, "text-emerald"],
            ["High Priority", radar?.last_run?.high_priority, "text-amber"],
            ["Breaking", radar?.last_run?.breaking, "text-crimson"],
          ].map(([label, val, color]) => (
            <div key={label} className="text-center">
              <div className={`font-mono text-2xl ${color}`}>{val ?? 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-admmuted mt-1 leading-tight">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* AUTO NEWSROOM — autonomous publishing pipeline */}
      <div className="bg-admcard border border-admborder p-5 mt-4" data-testid="auto-newsroom-panel">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${auto?.config?.auto_newsroom_enabled ? "bg-emerald animate-pulse-dot" : "bg-admmuted"}`} />
            <h3 className="text-sm font-bold uppercase tracking-widest text-admtext flex items-center gap-2">
              <Bot size={16} /> AUTO NEWSROOM — {auto?.config?.auto_newsroom_enabled ? "ACTIVE" : "OFF"}
            </h3>
            <span data-testid="auto-mode-badge" className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${auto?.config?.auto_publish ? "bg-emerald/15 text-emerald" : "bg-amber/15 text-amber"}`}>
              {auto?.config?.auto_publish ? "LIVE AUTO-PUBLISH ON" : "TEST MODE · AUTO-PUBLISH OFF"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runAuto} disabled={autoRunning || !auto?.config?.auto_newsroom_enabled} data-testid="run-auto-newsroom"
              className="flex items-center gap-2 bg-cyan text-admbg px-4 py-2 text-xs font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-50">
              <Bot size={14} className={autoRunning ? "animate-pulse" : ""} /> Run Auto Newsroom (All ≥70)
            </button>
            <button onClick={() => toggleAutoNewsroom(!auto?.config?.auto_newsroom_enabled)} data-testid="toggle-auto-newsroom"
              className={`w-11 h-6 rounded-full transition-colors relative ${auto?.config?.auto_newsroom_enabled ? "bg-emerald" : "bg-admborder"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${auto?.config?.auto_newsroom_enabled ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {AUTO_COUNTERS.map(([k, label, color]) => (
            <div key={k} className="bg-admbg border border-admborder p-3 text-center" data-testid={`auto-counter-${k}`}>
              <div className={`font-mono text-2xl ${color}`}>{auto?.counters?.[k] ?? 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-admmuted mt-1 leading-tight">{label}</div>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-admtext2 mb-2">Story Journeys</h4>
          <div className="space-y-2 max-h-[440px] overflow-auto no-scrollbar" data-testid="auto-journeys">
            {(auto?.journeys || []).map((j) => (
              <div key={j.id} className="border border-admborder p-3 bg-admbg" data-testid={`auto-journey-${j.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {j.draft_id ? (
                      <Link to={`/admin/articles/${j.draft_id}`} className="text-sm text-admtext hover:text-cyan leading-snug">{j.headline}</Link>
                    ) : (
                      <span className="text-sm text-admtext leading-snug">{j.headline}</span>
                    )}
                    <div className="text-[11px] text-admmuted font-mono mt-0.5">Rel {j.relevance_score}/100 · Imp {j.importance_score}/10</div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 shrink-0 ${STAGE_STYLE[j.auto_stage] || "bg-admborder text-admtext2"}`}>
                    {STAGE_LABEL[j.auto_stage] || j.auto_stage || "—"}
                  </span>
                </div>
                {j.auto_log?.length ? (
                  <div className="font-mono text-[10px] text-admtext2 mt-2 space-y-0.5 border-t border-admborder pt-2">
                    {j.auto_log.map((l, i) => (
                      <div key={`${l.time}-${l.step}-${i}`} className="flex gap-2">
                        <span className="text-cyan shrink-0">{l.time}</span>
                        <span className="text-admmuted shrink-0 w-24">{l.step}</span>
                        <span className="text-admtext2">{l.detail}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            {!(auto?.journeys || []).length && (
              <div className="text-admmuted text-sm text-center py-6">No autonomous journeys yet. Click "Run Auto Newsroom" or run research discovery.</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* Schedule + settings */}
        <div className="bg-admcard border border-admborder p-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-3">Research Schedule</h3>
          <div className="space-y-2">
            {SCHEDULES.map(([val, label]) => (
              <button key={val} onClick={() => setSchedule(val)} data-testid={`schedule-${val}`}
                className={`w-full text-left px-3 py-2 text-sm border transition-colors flex items-center gap-2.5 ${config.schedule === val ? "border-cyan text-cyan bg-cyan/5" : "border-admborder text-admtext2 hover:text-admtext"}`}>
                <span className={`w-3.5 h-3.5 rounded-full border shrink-0 flex items-center justify-center ${config.schedule === val ? "border-cyan" : "border-admmuted"}`}>
                  {config.schedule === val && <span className="w-1.5 h-1.5 rounded-full bg-cyan" />}
                </span>
                {label}
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-admborder flex items-center justify-between">
            <div>
              <div className="text-sm text-admtext">Auto-publish</div>
              <div className="text-xs text-admmuted">Off by default</div>
            </div>
            <button onClick={() => toggleAutopub(!config.auto_publish)} data-testid="toggle-autopub"
              className={`w-11 h-6 rounded-full transition-colors relative ${config.auto_publish ? "bg-emerald" : "bg-admborder"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${config.auto_publish ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
        </div>

        {/* Activity log */}
        <div className="bg-admcard border border-admborder p-5 lg:col-span-2">
          <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-3 flex items-center gap-2"><Terminal size={14} /> Agent Activity Log</h3>
          <div className="font-mono text-xs space-y-1.5 max-h-[340px] overflow-auto no-scrollbar" data-testid="agent-activity-log">
            {lastRun?.log?.length ? lastRun.log.map((l, i) => (
              <div key={`${l.time}-${i}`} className="flex gap-3">
                <span className="text-cyan shrink-0">{l.time}</span>
                <span className="text-admtext2">{l.message}</span>
              </div>
            )) : <div className="text-admmuted">No activity yet. Run research to begin.</div>}
          </div>
        </div>
      </div>

      {/* Human approval queue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="bg-admcard border border-admborder">
          <div className="px-5 py-3 border-b border-admborder flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-widest text-amber">Awaiting Approval</h3>
            <span className="text-xs text-admmuted font-mono">{awaiting.length}</span>
          </div>
          <div className="max-h-[360px] overflow-auto no-scrollbar" data-testid="awaiting-approval-list">
            {awaiting.map((a) => (
              <div key={a.id} className="px-5 py-3 border-b border-admborder last:border-0">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/admin/articles/${a.id}`} className="text-sm text-admtext hover:text-cyan leading-snug">{a.headline}</Link>
                  {a.fact_check?.verdict === "review" && <span className="text-[9px] text-amber border border-amber/40 px-1 uppercase shrink-0">FC</span>}
                </div>
                <div className="text-xs text-admmuted font-mono mt-0.5">{a.category} · {a.content_type} · {timeAgo(a.updated_date || a.created_at)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => approve(a.id, "published")} data-testid={`approve-publish-${a.id}`} className="flex items-center gap-1 bg-emerald text-admbg px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider hover:opacity-90"><Send size={11} /> Approve & Publish</button>
                  <button onClick={() => approve(a.id, "scheduled")} data-testid={`approve-schedule-${a.id}`} className="flex items-center gap-1 border border-admborder text-admtext px-2.5 py-1 text-[11px] uppercase tracking-wider hover:border-cyan"><Calendar size={11} /> Schedule</button>
                  <button onClick={() => approve(a.id, "archived")} data-testid={`reject-${a.id}`} className="flex items-center gap-1 border border-admborder text-admmuted px-2.5 py-1 text-[11px] uppercase tracking-wider hover:border-crimson hover:text-crimson"><X size={11} /> Reject</button>
                </div>
              </div>
            ))}
            {!awaiting.length && <div className="px-5 py-8 text-admmuted text-sm text-center">Nothing awaiting approval. Create drafts from Candidate Stories.</div>}
          </div>
        </div>

        <div className="bg-admcard border border-admborder">
          <div className="px-5 py-3 border-b border-admborder flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-widest text-emerald">Recently Published</h3>
            <Link to="/admin/articles?status=published" className="text-xs text-cyan">All →</Link>
          </div>
          <div className="max-h-[360px] overflow-auto no-scrollbar">
            {published.map((a) => (
              <div key={a.id} className="px-5 py-3 border-b border-admborder last:border-0 flex items-center justify-between gap-3">
                <Link to={`/admin/articles/${a.id}`} className="text-sm text-admtext hover:text-cyan leading-snug">{a.headline}</Link>
                <a href={`/${a.category}/${a.slug}`} target="_blank" rel="noreferrer" className="text-admmuted hover:text-cyan shrink-0"><ExternalLink size={13} /></a>
              </div>
            ))}
            {!published.length && <div className="px-5 py-8 text-admmuted text-sm text-center">No published articles yet.</div>}
          </div>
        </div>
      </div>

      {/* Recent runs */}
      <div className="bg-admcard border border-admborder mt-4">
        <div className="px-5 py-3 border-b border-admborder flex items-center justify-between">
          <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2">Recent Research Runs</h3>
          <Link to="/admin/candidates" className="text-xs text-cyan">Candidate Stories →</Link>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-admmuted text-xs uppercase tracking-wider">
            <th className="px-5 py-2 font-medium">Started</th><th className="px-3 py-2 font-medium">Trigger</th>
            <th className="px-3 py-2 font-medium">Sources</th><th className="px-3 py-2 font-medium">Candidates</th>
            <th className="px-3 py-2 font-medium">Drafts</th><th className="px-3 py-2 font-medium">Status</th>
          </tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-admborder text-admtext2">
                <td className="px-5 py-2.5 font-mono text-xs">{timeAgo(r.started_at)}</td>
                <td className="px-3 py-2.5 capitalize">{r.trigger}</td>
                <td className="px-3 py-2.5 font-mono">{r.sources_checked}</td>
                <td className="px-3 py-2.5 font-mono text-crimson">{r.candidates_created}</td>
                <td className="px-3 py-2.5 font-mono text-cyan">{r.drafts_created}</td>
                <td className="px-3 py-2.5"><span className={r.status === "completed" ? "text-emerald" : r.status === "failed" ? "text-crimson" : "text-amber"}>{r.status}</span></td>
              </tr>
            ))}
            {!runs.length && <tr><td colSpan={6} className="px-5 py-6 text-admmuted">No runs yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
